import { Component, DestroyRef, HostListener, inject, ViewEncapsulation, OnInit, ViewChild, OnDestroy, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Pros } from 'src/providers/pros/pros';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { Loading } from 'src/shared/components/loading/loading';
import { Util } from 'src/shared/util/util';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { ItemNode, FlatNode, ChipFilter } from 'src/models/schedule.model';

import { ConfirmPage } from '../confirm/confirm.component';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { WindowService } from 'src/shared/providers/window.service';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';

import { debounceTime } from 'rxjs/operators';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { AddProPage } from 'src/pages/my-pros/add-pro/add-pro';
import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { BrowseProsPage } from 'src/pages/browse-pros/browse-pros';
import { CustomerGuestsTabComponent } from 'src/pages/my-pros/components/customer-guests-tab/customer-guests-tab.component';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { prosPagePath } from 'src/providers/guard/rental-routes.guard';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { DateTime as LuxonDateTime } from 'luxon';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { SubscriptionGateService } from 'src/shared/services/subscription-gate.service';

@Component({
    templateUrl: 'my-pros.html',
    styleUrls: ['./my-pros.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MyProsPage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly urlStateService = inject(UrlStateService);

  selectedTab = 'pros';
  tabs = [];

  get tabTitle(): string {
    return this.tabs.find(t => t.value === this.selectedTab)?.label ?? 'Contacts';
  }

  allowsFindNewPros: boolean;
  addressId: number;
  addressFilter: TidySelectNumberValueModel[];
  addressResponse: any;
  allPros: any;
  allProHeaders: any = this.getAllProsHeaders();
  backPage: string;
  categoryId: number;
  readonly desktopWidthContent: string = scrollContentWidth.NORMAL;
  errorMessage: string;
  isRentalClient = localStorage.getItem('isRentalClient') === 'true';
  isAllAddressesSelected: boolean;
  jobId: number;
  customerId: number;
  panelClosedSubscription: Subscription;
  prosServices: any;
  form: UntypedFormGroup;
  showLoadingSpinner: boolean;
  searchForm: UntypedFormGroup;
  hasPrivatePro: boolean;
  hasPros: boolean;
  prosData: any;
  loaded: boolean;
  editPrio = false;
  priorityListSentence: string;
  showDesktopHamburgerMenu: boolean;
  showExpandedDesktopHamburger: boolean;
  shownPros: any;
  serviceCategoryItems: any;
  allProsResponse: any;
  categories: any;
  showAboutThisPage: boolean;
  hasProperties = false;
  currentRows: any;
  expandedProProperties = new Set<number>();
  expandedProServices = new Set<number>();
  chips: ChipFilter[] = [];
  selectedFilters: { property: string[]; service: string[] } = { property: [], service: [] };
  private _transformer = (node: ItemNode, level: number) => ({
    expandable: !!node.children && node.children.length > 0,
    name: node.name,
    level,
    data: node,
  });
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  filterDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: FlatNode) => node.expandable;
  @HostListener("window:resize", ["$event"])
  async onResize(event) {
    this.showDesktopHamburgerMenu = event.target.innerWidth < 1010;
  }
  @ViewChild('tidyTable') tidyTable: any;
  @ViewChild(CustomerGuestsTabComponent) customerGuestsTab?: CustomerGuestsTabComponent;

  overlayTop: string;
  private _prosHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('prosHeader', { read: ElementRef }) set prosHeader(el: ElementRef) {
    this._prosHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    } else if (!el && this.overlayResizeObserver) {
      this.overlayResizeObserver.disconnect();
      this.overlayResizeObserver = null;
    }
  }
  private cdr = inject(ChangeDetectorRef);


  boundGoToAddProAllAddresses = this.goToAddProAllAddresses.bind(this);
  boundGoToFindNewPro = this.goToFindNewPro.bind(this);

  onHeaderAddAction() {
    if (this.selectedTab === 'guests') {
      this.customerGuestsTab?.goToAddGuest();
      return;
    }
    this.goToAddProAllAddresses();
  }

constructor(
    private client: Client,
    private currentAddress: CurrentAddress,
    private fb: UntypedFormBuilder,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private aboutThisPageService: AboutThisPageService,
    private onboardingDummyDataService: OnboardingDummyDataService,
    private pros: Pros,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private util: Util,
    public windowService: WindowService,
    private demoBlock: DemoBlockService,
    private subscriptionGate: SubscriptionGateService
  ) {
    this.form = this.fb.group({
      pros: [1, Validators.required]
    });
    this.searchForm = this.fb.group({
      search: ['']
    });
    this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => this.applyFilters());
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('pros');
  }

  async ngOnInit() {
    const vendorsTab = { value: 'vendors', label: 'Vendors', icon: 'assets/svg/briefcase-outline.svg' };
    this.tabs = this.isRentalClient
      ? [
          { value: 'pros', label: 'Pros', icon: 'assets/svg/briefcase-outline.svg' },
          { value: 'guests', label: 'Guests/Tenants', icon: 'assets/svg/people-outline.svg' },
          vendorsTab,
        ]
      : [
          { value: 'pros', label: 'Pros', icon: 'assets/svg/briefcase-outline.svg' },
          vendorsTab,
        ];
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['tab'] && this.tabs.some(t => t.value === params['tab'])) {
          this.selectedTab = params['tab'];
        }
      });
    try {
      this.loadPage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.urlStateService.updateQueryParams({ tab });
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('pros');
    await this.getPageData();
    this.showLoadingSpinner = false;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute(prosPagePath()).then(async () => {
        await this.loadPage(true);
      });
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.overlayResizeObserver = null;
    this.allPros = [];
    this.shownPros = [];
    this.currentRows = [];
    this.cdr.markForCheck();
    this.loadPage(true);
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._prosHeader) {return;}
      const top = this._prosHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.cleanup();
    this.overlayResizeObserver?.disconnect();
  }

  cleanup() {
    if (this.panelClosedSubscription) {
      this.panelClosedSubscription.unsubscribe();
    }
  }

  async getPageData() {
    this.backPage = localStorage.getItem('myProsBackPage') || 'more';
    this.prosServices = [{
      viewValue: 'Cleaning',
      value: 1
    }];
    await this.getAddresses();
    this.hasProperties = this.addressResponse?.length > 0;
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.categories = await this.pros.getServiceCategoriesOrFetch();
    this.serviceCategoryItems = this.getServiceCategoryItems(this.categories);
    this.isAllAddressesSelected = localStorage.getItem('isAllAddressesSelected') === 'true' && this.windowService.isDesktopRes;
    if (!this.showAboutThisPage && this.hasProperties) {
      this.hasPrivatePro = await this.pros.checkIfHasPrivatePro();
      this.allPros = await this.getAllPros();
    } else {
      this.hasPrivatePro = false;
      this.allPros = this.showAboutThisPage ? await this.getAllPros() : [];
    }
    this.currentRows = this.allPros;
    this.shownPros = this.allPros;
    this.allProHeaders = this.getAllProsHeaders();
    this.buildFilterItems();
    this.showDesktopHamburgerMenu = window.innerWidth < 995;
    this.loaded = true;
  }

  async getProsData() {
    this.categoryId = parseInt(localStorage.getItem('selectedCategoryId')) || 1;
    this.prosData = await this.getPros(this.categoryId);
    this.hasPrivatePro = this.checkIfHasPrivateProsInAddress();
    this.hasPros = this.checkIfHasProsInAddress();
    this.allowsFindNewPros = this.checkIfAllowsFindNewPros();
    this.priorityListSentence = this.getPriorityListSentence();
  }

  checkIfAllowsFindNewPros() {
    const isBlocked = this.prosData.blocked.find((item) => item.object_type === 'dynamic_sa')
    return !isBlocked;
  }

  updateSearch({ search }) {
    if (search.length < 2 || search === '') {
      return this.shownPros = this.allPros;
    }
    const term = search.toLowerCase();
    this.shownPros = this.allPros.filter((pro) => {
      const isPropertyMatch = pro['Properties'].find((property) => property.value.toLowerCase().includes(term));
      const isServiceMatch = pro['Services'].find((service) => service.value.toLowerCase().includes(term));
      if (pro['Name'].value.toLowerCase().includes(term) || isPropertyMatch || isServiceMatch) {
        return pro;
      }
    });
  }

  buildFilterItems() {
    const propertyChildren: ItemNode[] = [];
    const serviceChildren: ItemNode[] = [];
    const propertySet = new Set<string>();
    const serviceSet = new Set<string>();
    this.allPros?.forEach((pro) => {
      pro['Properties']?.forEach((p) => {
        if (p.value && !propertySet.has(p.value)) {
          propertySet.add(p.value);
          propertyChildren.push({ name: p.value, value: p.value, checked: false });
        }
      });
      pro['Services']?.forEach((s) => {
        if (s.value && !serviceSet.has(s.value)) {
          serviceSet.add(s.value);
          serviceChildren.push({ name: s.value, value: s.value, checked: false });
        }
      });
    });
    const treeData: ItemNode[] = [];
    if (propertyChildren.length) {
      treeData.push({ name: 'Properties', children: propertyChildren });
    }
    if (serviceChildren.length) {
      treeData.push({ name: 'Services', children: serviceChildren });
    }
    treeData.forEach((parent, index) => {
      parent.children?.forEach((child) => { child.parentId = index; });
    });
    this.filterDataSource.data = treeData;
  }

  checkFilterNode(node: FlatNode, event: MatCheckboxChange) {
    const parentNode = this.filterDataSource.data[node.data.parentId];
    const parentName = parentNode?.name === 'Properties' ? 'property' : 'service';
    const value = node.data.value as string;
    if (event.checked) {
      if (!this.selectedFilters[parentName].includes(value)) {
        this.selectedFilters[parentName].push(value);
      }
      this.chips.push({
        displayParentName: parentNode.name,
        parentName,
        name: node.name,
        value,
        icon: parentName === 'property' ? 'home' : 'build',
      });
    } else {
      this.selectedFilters[parentName] = this.selectedFilters[parentName].filter((v) => v !== value);
      this.chips = this.chips.filter((c) => !(c.parentName === parentName && c.value === value));
    }
    this.applyFilters();
  }

  getFilterNodeChecked(node: FlatNode): boolean {
    const parentNode = this.filterDataSource.data[node.data?.parentId];
    const parentName = parentNode?.name === 'Properties' ? 'property' : 'service';
    return this.selectedFilters[parentName]?.includes(node.data.value as string) ?? false;
  }

  removeFilterChip(chip: ChipFilter) {
    this.selectedFilters[chip.parentName] = this.selectedFilters[chip.parentName].filter((v) => v !== chip.value);
    this.chips = this.chips.filter((c) => !(c.parentName === chip.parentName && c.value === chip.value));
    this.applyFilters();
  }

  applyFilters() {
    let filtered = this.allPros;
    const search = this.searchForm?.value?.search || '';
    if (search.length >= 2) {
      const term = search.toLowerCase();
      filtered = filtered.filter((pro) => {
        const isPropertyMatch = pro['Properties'].find((property) => property.value.toLowerCase().includes(term));
        const isServiceMatch = pro['Services'].find((service) => service.value.toLowerCase().includes(term));
        return pro['Name'].value.toLowerCase().includes(term) || isPropertyMatch || isServiceMatch;
      });
    }
    if (this.selectedFilters.property.length > 0) {
      filtered = filtered.filter((pro) =>
        pro['Properties']?.some((p) => this.selectedFilters.property.includes(p.value))
      );
    }
    if (this.selectedFilters.service.length > 0) {
      filtered = filtered.filter((pro) =>
        pro['Services']?.some((s) => this.selectedFilters.service.includes(s.value))
      );
    }
    this.shownPros = filtered;
  }

  async getAllPros() {
    this.allProsResponse = this.showAboutThisPage ? this.onboardingDummyDataService.getMyProsMockData() : await this.pros.getAllPros();
    const allProsArray = [];
    this.allProsResponse.map((pro) => {
      let addressesViewValue = '';
      let _addressesSearchValue = '';
      let addedAddressesQuantity = 0;
      const services = [];
      const properties = [];
      pro.address_homekeeper_priorities?.map((item, _index) => {
        const address = this.addressResponse.find((addr) => addr.id === item.address_id);
        let addressName = address?.address_name ? address?.address_name : address?.address1;
        if (this.showAboutThisPage) {
          addressName = item?.address_name || '159 Morning Star';
        }
        if (!addressesViewValue.includes(addressName)) {
          _addressesSearchValue += `${addressName  }, `;
          if (addedAddressesQuantity < 5) {
            addressesViewValue += `${addressName  }, `;
            addedAddressesQuantity++;
          }
        }
        if (!properties.find((p) => p.value === addressName)) {
          properties.push({
            value: addressName,
            color: address?.color || '#1E2125',
            action: null,
            addressId: address?.id
          });
        }
        if (this.showAboutThisPage && item?.service_name) {
          if (!services.find((s) => s.value === item.service_name)) {
            services.push({
              value: new TranslationPipe().transform(item.service_name),
              icon: item.service_icon || null,
              action: null,
            });
          }
          return;
        }
        const service = this.categories.find((category) => category.id === item.service_type_id);
        if (service) {
          service.name = !service?.name ? '' : (service?.name === 'Regular Cleaning' ? new TranslationPipe().transform('Cleaning') : new TranslationPipe().transform(service?.name));
          if (!services.find((s) => s.value === service?.name)) {
            services.push({
              value: new TranslationPipe().transform(service.name),
              icon: service.icon_url,
              action: null,
            });
          }
        } else if (services?.length === 0 && this.showAboutThisPage) {
          services.push({
            value: 'Cleaning',
            icon: 'assets/img/mop.png',
            action: null,
          });
        }
      });
      allProsArray.push({
        'Name': {
          value: pro.name,
          action: null,
          initials: true
        },
        'Properties': properties,
        'Services': services,
        'Last Job': {
          value: this.getJobString(pro.last_job),
          action: null
        },
        'Next Job': {
          value: this.getJobString(pro.next_job),
          action: null
        },
        'last-column': {
          value: '',
          action: null
        },
        'teamId': pro.main_team_id,
        'proId': pro.id
      });
    });
    return allProsArray;
  }

  buildPropertiesArray() {
    const properties = [];
    this.addressResponse.map((address) => {
      properties.push({
        value: address.address_name,
        color: '#9C90E9',
        action: null,
      });
    });
    return properties;
  }

  getJobString(job) {
    if (!job) {
      return '';
    }
    const date = new ParseDateTimePipe().transform(job.date, 'MMM d');
    const time = new ParseDateTimePipe().transform(job.start_time, 'h:mma');
    return `${date  } ${  time}`;
  }

  getAllProsHeaders() {
    return [
      'Name',
      'Properties',
      'Services',
      'Last Job',
      'Next Job',
      'last-column'
    ];
  }

  async getPros(id) {
    let pros = await this.pros.getPros(id);
    if (this.categoryId !== 1) {
      pros = this.removeFindNewProsCard(pros);
    }
    return pros;
  }

  removeFindNewProsCard(pros) {
    const approved: any = [];
    pros.approved.map(pro => {
      if (pro.object_type !== 'dynamic_sa') {
        approved.push(pro);
      }
    });
    pros.approved = approved;
    return pros;
  }

  getServiceCategoryItems(serviceCategories) {
    const array: any = [];
    serviceCategories.map((service) => {
      array.push({
        viewValue: service.name,
        value: service.id
      });
    });
    return array;
  }

  async changeServiceCategory(id) {
    this.categoryId = id;
    localStorage.setItem('selectedCategoryId', id);
    this.prosData = await this.getPros(id);
  }

  async getAddresses() {
    this.addressResponse = await this.client.getAddresses();
    this.addressFilter = await this.client.parseAddressList(this.addressResponse, true);
    this.addressId = await this.client.getSelectedAddressId(this.addressResponse);
  }

  async getDisplayData() {
    this.prosData = await this.getPros(this.form.value.pros);
    this.priorityListSentence = this.getPriorityListSentence();
  }

  @Loading('', true)
  async changeAddress(addressId) {
    if (addressId === 0) {
      return this.navCtrl.navigateForward('add-property');
    }
    this.currentAddress.addressId = addressId;
    await this.getDisplayData();
  }

  checkIfHasProsInAddress(): boolean {
    const allObjects: any = [];
    this.prosData.favorited.map((object) => allObjects.push(object));
    this.prosData.approved.map((object) => allObjects.push(object));
    this.prosData.blocked.map((object) => allObjects.push(object));
    const isTidyFindNewProsAppearing = allObjects.find((object) => object.object_type === 'dynamic_da');
    let numberOfPros = this.prosData.favorited.length + this.prosData.approved.length + this.prosData.blocked.length;
    if (isTidyFindNewProsAppearing) {
      numberOfPros -= 1;
    }
    return numberOfPros > 0 || this.categoryId !== 1;
  }

  checkIfHasPrivateProsInAddress(): boolean {
    let hasPrivatePro = false;
    this.prosData.approved.map((pro) => {
      if (pro?.object?.is_private) {
        hasPrivatePro = true;
      }
    });
    this.prosData.favorited.map((pro) => {
      if (pro?.object?.is_private) {
        hasPrivatePro = true;
      }
    });
    return hasPrivatePro;
  }

  isAbleToBlock(item) {
    return (item?.object_type !== 'dynamic_sa' || this.hasPrivatePro);
  }

  handleNotAbleToBlockPro(): void {
    this.util.showWarning('You must have at least 1 private pro on your list to block Find New Pro.', 7000);
  }

  blockPro() {
    this.modalCtrl.dismiss(true);
  }

  @Loading('', true)
  async updatePriorities() {
    await this.pros.updateProsPriorities(this.prosData, this.form.controls.pros.value);
    this.priorityListSentence = this.getPriorityListSentence();
  }

  goToVendorCompliance() {
    this.navCtrl.navigateForward('compliance');
  }

  goToTidyFindNewPro(allowsFindNewPros) {
    const params = {
      allowsFindNewPros,
      prosData: this.prosData,
      hasPrivatePro: this.hasPrivatePro
    }
    this.navCtrl.navigateForward('tidy-find-new-pro', params);
  }

  goToTidyFindNewProFromOnboarding() {
    const params = {
      cameFromMyProsOnboardingPage: true,
      serviceCategoryItems: this.serviceCategoryItems,
      allowsFindNewPros: true
    }
    this.navCtrl.navigateForward('tidy-find-new-pro', params);
  }

  async goToPro(proId) {
    if (this.demoBlock.block()) { return; }
    if (await this.subscriptionGate.gate()) { return; }
    const pro = this.allProsResponse.find((p) => p.id === proId);
    let selectedAddressId = parseInt(localStorage.getItem('addressId'));
    let categoryId;
    let address;
    pro.address_homekeeper_priorities.map((priority) => {
      if (priority.address_id === selectedAddressId) {
        address = this.addressResponse.find((addr) => addr.id === selectedAddressId);
        categoryId = priority.service_type_id;
      }
    });
    if (!address && pro.address_homekeeper_priorities?.length > 0) {
      selectedAddressId = pro.address_homekeeper_priorities[0].address_id;
      localStorage.setItem('addressId', selectedAddressId.toString());
      address = this.addressResponse.find((addr) => addr.id === selectedAddressId);
      pro.address_homekeeper_priorities.map((priority) => {
        if (priority.address_id === address?.id) {
          categoryId = priority.service_type_id;
        }
      });
    }
    pro.address_homekeeper_priorities.map((item) => {
      const serviceName = this.categories.find((category) => category.id === item.service_type_id)?.name;
      if (serviceName === 'Regular Cleaning' && item.address_id === this.addressId) {categoryId = item.service_type_id;}
    });
    localStorage.setItem('myProBackPage', 'contacts');
    const params = {
      proId,
      address,
      categoryId
    };
    this.goToPage(`my-pro/${proId}`, params, MyProPage);
  }

  goToProSettings() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const proNames = {};
    const proIds = {};
    (this.allPros || []).map((pro) => {
      proIds[pro.teamId] = pro.proId;
      proNames[pro.teamId] = pro['Name'].value;
    });
    this.storage.save('proNames', proNames);
    this.storage.save('proIds', proIds);
    this.navCtrl.navigateForward('pro-settings');
  }

  async addPro() {
    if (await this.subscriptionGate.gate()) { return; }
    const params = {
      serviceCategoryItems: this.serviceCategoryItems,
      categoryId: this.categoryId
    }
    this.goToPage('add-pro', params, AddProPage);
  }

  async goToAddProAllAddresses() {
    if (this.demoBlock.block()) { return; }
    if (await this.subscriptionGate.gate()) { return; }
    const params = {
      serviceCategoryItems: this.serviceCategoryItems,
      noAddressSelected: true
    }
    this.goToPage('add-pro', params, AddProPage);
  }

  async goToFindNewPro() {
    if (this.showAboutThisPage) {
      return;
    }
    if (await this.subscriptionGate.gate()) { return; }
    const params = {
      address: this.addressResponse.find(ad => ad.id === this.addressId),
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      this.rightSidePanelService.openRightSidePanel(BrowseProsPage);
    } else {
      this.navCtrl.navigateForward('browse-pros', params);
    }
  }

  learnMoreAddPros() {
    const url = 'https://help.tidy.com/my-pros';
    this.util.openUrl(url);
  }

  learnMorePros() {
    const url = 'https://help.tidy.com/my-pros';
    this.util.openUrl(url);
  }

  @Loading('', true)
  async refresh() {
    this.prosData = await this.getPros(this.form.value.pros);
  }

  getPriorityListSentence() {
    const address = this.addressResponse.find(ad => ad.id === this.addressId);
    const addressName = address.address_name ? address.address_name : address.address1;
    const priorityListSentence = this.pros.updatePriorityListSentence(this.prosData);
    return `For <u>${  addressName  }</u> ${  priorityListSentence}`;
  }

  async removeProFromList(pro) {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      title: `${new TranslationPipe().transform('Remove')  } ${  pro?.object?.first_name  } ${  new TranslationPipe().transform('From List?')}`,
      body: 'The pro will be removed from the list for this address for this service type. You cannot undo this action.',
      backText: 'Go Back',
      confirmText: 'Remove from List',
      confirmAction: this.confirmRemoveProFromList.bind(this, pro),
      confirmButtonClass: 'secondary',
      blockAction: this.confirmBlockPro.bind(this, pro),
    }
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await modal.present();
  }

  async confirmRemoveProFromList(pro) {
    if (this.showAboutThisPage) {
      return;
    }
    try {
      const payload = {
        service_type_id: this.form.value.pros,
        homekeeper_ids: [pro?.object?.id]
      }
      await this.pros.removeProFromAddressList(this.addressId, payload);
      this.prosData = await this.getPros(this.categoryId);
      this.hasPros = this.checkIfHasProsInAddress();
      this.hasPrivatePro = this.checkIfHasPrivateProsInAddress();
      this.priorityListSentence = this.pros.updatePriorityListSentence(this.prosData);
      this.modalCtrl.dismiss(true);
      this.util.showSuccess('Pro removed from list');
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async confirmBlockPro() {
    if (this.showAboutThisPage) {
      return;
    }
    const approvedIndex = this.prosData.approved.findIndex(pro => pro.object_type === 'dynamic_sa');
    const favoritedIndex = this.prosData.favorited.findIndex(pro => pro.object_type === 'dynamic_sa');
    let removedElement: any[];
    if (approvedIndex >= 0) {
      removedElement = this.prosData.approved.splice(approvedIndex, 1);
    }
    if (favoritedIndex >= 0) {
      removedElement = this.prosData.favorited.splice(favoritedIndex, 1);
    }
    if (removedElement) {
      const indexToAdd = 0;
      this.prosData.blocked.splice(indexToAdd, 0, ...removedElement);
    }
    try {
      const serviceTypeId = 1;
      await this.pros.updateProsPriorities(this.prosData, serviceTypeId);
      this.prosData = await this.getPros(this.categoryId);
      this.hasPros = this.checkIfHasProsInAddress();
      this.hasPrivatePro = this.checkIfHasPrivateProsInAddress();
      this.priorityListSentence = this.pros.updatePriorityListSentence(this.prosData);
      this.modalCtrl.dismiss(true);
      this.util.showSuccess('Pro removed from list');
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  toggleDesktopHamburger() {
    this.showExpandedDesktopHamburger = !this.showExpandedDesktopHamburger;
  }

  async goToPage(url, params, component) {
    if (this.showAboutThisPage) {
      return;
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        const didAddPro = await this.storage.retrieve('didAddPro');
        if (didAddPro) {
          await this.storage.save('didAddPro', false);
          // TanStack Query handles cache invalidation, just refresh page data
          this.getPageData();
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  onTableColumnClick(event: {column: string, element: any}) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.goToPro(event.element.proId);
  }

  toggleProProperties(proId: number) {
    if (this.expandedProProperties.has(proId)) {
      this.expandedProProperties.delete(proId);
    } else {
      this.expandedProProperties.add(proId);
    }
  }

  getVisibleProperties(pro: any): any[] {
    const properties = pro['Properties'] || [];
    if (this.expandedProProperties.has(pro.proId) || properties.length <= 5) {
      return properties;
    }
    return properties.slice(0, 5);
  }

  toggleProServices(proId: number) {
    if (this.expandedProServices.has(proId)) {
      this.expandedProServices.delete(proId);
    } else {
      this.expandedProServices.add(proId);
    }
  }

  getVisibleServices(pro: any): any[] {
    const services = pro['Services'] || [];
    if (this.expandedProServices.has(pro.proId) || services.length <= 5) {
      return services;
    }
    return services.slice(0, 5);
  }

  sortChanged(sort) {
    this.allPros.sort((a, b) => {
      if (sort.active === 'Last Job' || sort.active === 'Next Job') {
        if (!a[sort.active].value) {return sort.direction === 'asc' ? -1 : 1;}
        if (!b[sort.active].value) {return sort.direction === 'asc' ? 1 : -1;}

        const [dateStrA, timeStrA] = a[sort.active].value.split(' ');
        const [dateStrB, timeStrB] = b[sort.active].value.split(' ');

        const dateA = LuxonDateTime.fromFormat(`${dateStrA  } ${  timeStrA}`, 'M/d/yy h:mma');
        const dateB = LuxonDateTime.fromFormat(`${dateStrB  } ${  timeStrB}`, 'M/d/yy h:mma');

        return sort.direction === 'asc' ?
          dateA.toMillis() - dateB.toMillis() :
          dateB.toMillis() - dateA.toMillis();
      } else {
        if (!a[sort.active]?.value) {return sort.direction === 'asc' ? -1 : 1;}
        if (!b[sort.active]?.value) {return sort.direction === 'asc' ? 1 : -1;}

        const valueA = this.parseRowNumberValue(a[sort.active].value);
        const valueB = this.parseRowNumberValue(b[sort.active].value);

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sort.direction === 'asc' || sort.direction === '' ? valueA - valueB : valueB - valueA;
        } else {
          const strA = String(valueA).toLowerCase();
          const strB = String(valueB).toLowerCase();
          if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
          if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
          return 0;
        }
      }
    });
    this.currentRows = this.allPros;
  }

  parseRowNumberValue(value: string): string | number {
    if (value.includes('$')) {
      const numberValue = Number(value.replace(/\$|\.|\s/g, ''));
      return isNaN(numberValue) ? value : numberValue;
    }
    return value;
  }

  exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.tidyTable) {
      this.tidyTable.exportCsv(this.tidyTable.tabledataSource);
    } else {
      this.util.showError('No data to export', 5000);
    }
  }

}
